import React, { useEffect, useState } from "react";
import { Box, Typography, Card, CardContent, LinearProgress, IconButton } from "@mui/material";
import { Link } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import BarChartIcon from "@mui/icons-material/BarChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FornecedorService from "../../service/fornecedor_service";
import ProdutoService from "../../service/produto_service";
import ColetaService from "../../service/coleta_service";
import { hojeISO } from "../../utils/dateUtils";
import { useUser } from "../../context/UserContext";
import { ehDistribuicao } from "../../utils/licenca";
import UpdatesPopUp from "../UpdatesPopUp";

// `step` identifica o passo (é o que `isStepComplete` consulta), não a posição:
// a licença de distribuição não tem coleta e a numeração mostrada é recontada
// depois da filtragem, senão a tela pularia do 2 para o 5.
const workflowSteps = [
	{ step: 1, to: "/fornecedores", label: "Cadastrar Fornecedores", description: "Registre seus produtores com telefone e produtos fornecidos", icon: PeopleIcon, color: "#1a9e8f" },
	{ step: 2, to: "/distribuidora", label: "Cadastrar Produtos", description: "Liste os produtos que serão comercializados", icon: InventoryIcon, color: "#f5a623" },
	{ step: 3, to: "/coletas", label: "Registrar Demanda", description: "Defina a necessidade de cada produto por dia", icon: AssignmentIcon, color: "#3498db" },
	{ step: 4, to: "/coletas", label: "Acompanhar Coletas", description: "Veja as previsões dos fornecedores em tempo real", icon: TrendingUpIcon, color: "#1a9e8f" },
	{ step: 5, to: "/compras", label: "Receber Produtos", description: "Registre entradas de produtos dos fornecedores", icon: LocalShippingIcon, color: "#27ae60" },
	{ step: 6, to: "/relatorios", label: "Analisar Relatórios", description: "Acompanhe volumes, participação e tendências", icon: BarChartIcon, color: "#5a7a88" },
];

const PASSOS_DE_COLETA = [3, 4];

function StatCard({ title, value, icon, subtitle, bgColor = "#1a9e8f" }) {
	return (
		<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", height: "100%" }}>
			<CardContent sx={{ p: { xs: 2, md: 2.5 }, height: "100%", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="body2" sx={{ color: "#5a7a88", fontSize: { xs: "0.75rem", md: "0.875rem" }, lineHeight: 1.2 }}>
						{title}
					</Typography>
					<Typography variant="h4" sx={{ fontWeight: 700, color: "#0f4557", fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.2, mt: 0.5, fontSize: { xs: "1.5rem", md: "1.875rem" } }}>
						{value}
					</Typography>
					{subtitle && (
						<Typography variant="caption" sx={{ color: "#5a7a88", fontSize: { xs: "0.625rem", md: "0.75rem" }, mt: 0.5, display: "block" }}>
							{subtitle}
						</Typography>
					)}
				</Box>
				<Box sx={{
					bgcolor: `${bgColor}15`,
					borderRadius: "12px",
					width: { xs: 40, md: 44 },
					height: { xs: 40, md: 44 },
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
				}}>
					{React.cloneElement(icon, { sx: { color: bgColor, fontSize: { xs: 20, md: 24 } } })}
				</Box>
			</CardContent>
		</Card>
	);
}

function Home() {
	const [showTip, setShowTip] = useState(true);
	const [stats, setStats] = useState({
		fornecedores: 0,
		produtos: 0,
		coletas: 0,
		coletasConfirmadas: 0,
	});
	const [recentCollections, setRecentCollections] = useState([]);
	const [weeklySummary, setWeeklySummary] = useState([]);
	const [, setLoading] = useState(true);
	const { user } = useUser();

	// A licença de distribuição não contratou a operação de coleta: aqui isso
	// não é só esconder card, é não chamar endpoint que a API vai recusar.
	const semColetas = ehDistribuicao(user);
	const passosVisiveis = semColetas
		? workflowSteps.filter((item) => !PASSOS_DE_COLETA.includes(item.step))
		: workflowSteps;

	useEffect(() => {
		// O acesso já é garantido pelo guard de rota (access_token).
		// O perfil em si fica no UserContext, não é mais lido do localStorage.
		loadDashboardData(semColetas);
	}, [semColetas]);

	const loadDashboardData = async (pularColetas) => {
		try {
			setLoading(true);
			const unidadeId = localStorage.getItem("selected_unidade_id");
			if (!unidadeId) return;

			const fornecedorService = new FornecedorService();
			const produtoService = new ProdutoService();
			const coletaService = new ColetaService();

			// Buscar estatísticas
			const [fornecedoresRes, produtosRes] = await Promise.all([
				fornecedorService.getByUnidade(unidadeId).catch(() => ({ data: [] })),
				produtoService.getByUnidade(unidadeId, "entrada").catch(() => ({ data: [] })),
			]);

			const today = hojeISO();
			let coletasStats = { totalColetas: 0, confirmadas: 0, pendentes: 0 };
			if (!pularColetas) {
				try {
					const estatisticasRes = await coletaService.getEstatisticas(unidadeId, today);
					coletasStats = estatisticasRes.data || coletasStats;
				} catch (e) {
					console.log("Estatísticas ainda não disponíveis");
				}
			}

			setStats({
				fornecedores: fornecedoresRes.data?.length || 0,
				produtos: (Array.isArray(produtosRes) ? produtosRes : produtosRes.data || []).length,
				coletas: coletasStats.totalColetas || 0,
				coletasConfirmadas: coletasStats.confirmadas || 0,
			});

			if (pularColetas) return;

			// Buscar coletas recentes
			try {
				const coletasRes = await coletaService.getColetasPorData(unidadeId, today);
				const coletas = coletasRes.data?.data || [];
				setRecentCollections(coletas.slice(0, 3).map(c => ({
					supplier: c.fornecedor?.nome || "Sem fornecedor",
					product: c.produtoUnidadeDistribuidora?.cultivo?.cultura || "Produto",
					qty: `${c.quantidade} ${c.unidadeMedida?.sigla || ""}`,
					date: new Date(c.data).toLocaleDateString("pt-BR", { timeZone: "UTC" }),
					status: c.status,
				})));
			} catch (e) {
				console.log("Coletas ainda não disponíveis");
			}

			// Buscar resumo semanal
			try {
				const resumoRes = await coletaService.getResumoSemanal(unidadeId);
				setWeeklySummary(resumoRes.data || []);
			} catch (e) {
				console.log("Resumo semanal ainda não disponível");
			}

		} catch (error) {
			console.error("Erro ao carregar dashboard:", error);
		} finally {
			setLoading(false);
		}
	};

	const isStepComplete = (step) => {
		if (step === 1) return stats.fornecedores > 0;
		if (step === 2) return stats.produtos > 0;
		if (step === 3) return stats.coletas > 0;
		if (step === 4) return stats.coletasConfirmadas > 0;
		return false;
	};

	return (
		<AppLayout currentPage="home">
			<Box sx={{ width: "100%" }}>
					{/* Header */}
					<Box sx={{ mb: 3, display: { xs: "none", md: "block" } }}>
						<Typography variant="h4" sx={{ color: "#0f4557", fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
							Dashboard
						</Typography>
					</Box>

					{/* Stats Cards */}
					<Box sx={{
						display: "grid",
						gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: semColetas ? "repeat(2, 1fr)" : "repeat(4, 1fr)" },
						gap: { xs: 1.5, md: 2 },
						mb: 3
					}}>
						<StatCard title="Fornecedores" value={stats.fornecedores} icon={<PeopleIcon />} />
						<StatCard title="Produtos" value={stats.produtos} icon={<InventoryIcon />} bgColor="#f5a623" />
						{!semColetas && (
							<>
								<StatCard title="Coletas Hoje" value={stats.coletas} icon={<TrendingUpIcon />} subtitle="do dia" />
								<StatCard title="Confirmadas" value={stats.coletasConfirmadas} icon={<CheckCircleIcon />} bgColor="#27ae60" />
							</>
						)}
					</Box>

					{/* Tip Card */}
					{showTip && (
						<Card sx={{ mb: 3, borderRadius: "16px", bgcolor: "#1a9e8f0d", border: "1px solid #1a9e8f33" }}>
							<CardContent sx={{ p: 2.5 }}>
								<Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
									<Box sx={{ bgcolor: "#1a9e8f15", borderRadius: "12px", p: 1.5 }}>
										<LightbulbIcon sx={{ color: "#1a9e8f" }} />
									</Box>
									<Box sx={{ flex: 1 }}>
										<Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0f4557", mb: 0.5 }}>
											Primeira vez por aqui?
										</Typography>
										<Typography variant="body2" sx={{ color: "#5a7a88", fontSize: "0.75rem" }}>
											Siga os {passosVisiveis.length} passos abaixo para configurar seu sistema de fornecimento.
										</Typography>
									</Box>
									<IconButton size="small" onClick={() => setShowTip(false)}>
										<CloseIcon fontSize="small" sx={{ color: "#5a7a88" }} />
									</IconButton>
								</Box>
							</CardContent>
						</Card>
					)}

					{/* Workflow Steps */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557", mb: 0.5 }}>
							Como usar o sistema
						</Typography>
						<Typography variant="body2" sx={{ color: "#5a7a88", mb: 2, fontSize: "0.75rem" }}>
							Siga a sequência para configurar e operar o fornecimento
						</Typography>
						<Box sx={{
							display: "grid",
							gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
							gap: 1.5
						}}>
							{passosVisiveis.map((item, indice) => {
								const complete = isStepComplete(item.step);
								const numero = indice + 1;
								return (
									<Link key={item.step} to={item.to} style={{ textDecoration: "none" }}>
										<Card sx={{
											borderRadius: "16px",
											boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
											border: "1px solid #dfe3e8",
											transition: "all 0.2s",
											height: "100%",
											"&:hover": {
												boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
												borderColor: "#1a9e8f50",
											}
										}}>
											<CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
												<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
													<Box sx={{ position: "relative" }}>
														<Box sx={{
															bgcolor: `${item.color}15`,
															borderRadius: "16px",
															width: { xs: 40, md: 48 },
															height: { xs: 40, md: 48 },
															display: "flex",
															alignItems: "center",
															justifyContent: "center"
														}}>
															<item.icon sx={{ color: item.color, fontSize: { xs: 20, md: 24 } }} />
														</Box>
														<Box sx={{
															position: "absolute",
															top: -6,
															left: -6,
															width: 20,
															height: 20,
															borderRadius: "50%",
															bgcolor: "white",
															border: "1px solid #dfe3e8",
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
														}}>
															{complete ? (
																<CheckCircleIcon sx={{ fontSize: 16, color: "#1a9e8f" }} />
															) : (
																<Typography sx={{ fontSize: 10, fontWeight: 700, color: "#8e9bae" }}>
																	{numero}
																</Typography>
															)}
														</Box>
													</Box>
													<Box sx={{ minWidth: 0 }}>
														<Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#0f4557", lineHeight: 1.3, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
															{item.label}
														</Typography>
														<Typography variant="caption" sx={{ color: "#5a7a88", display: { xs: "none", sm: "block" }, fontSize: { xs: "0.625rem", md: "0.75rem" }, lineHeight: 1.4 }}>
															{item.description}
														</Typography>
													</Box>
												</Box>
											</CardContent>
										</Card>
									</Link>
								);
							})}
						</Box>
					</Box>

					{/* Recent Collections & Weekly Summary — os dois painéis saem das
					    coletas, então somem junto com elas na licença de distribuição. */}
					{!semColetas && (
					<Box sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
						gap: { xs: 2, md: 3 }
					}}>
						{/* Recent Collections */}
						<Box>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557" }}>
									Coletas Recentes
								</Typography>
								<Link to="/coletas" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 4, color: "#1a9e8f", fontSize: "0.875rem" }}>
									Ver todas <ArrowForwardIcon sx={{ fontSize: 14 }} />
								</Link>
							</Box>
							<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
								{recentCollections.length === 0 ? (
									<CardContent sx={{ p: 4, textAlign: "center" }}>
										<Typography variant="body2" sx={{ color: "#8e9bae" }}>
											Nenhuma coleta registrada hoje
										</Typography>
									</CardContent>
								) : (
									recentCollections.map((c, i) => (
										<Box key={i} sx={{
											p: 2,
											borderBottom: i < recentCollections.length - 1 ? "1px solid #dfe3e8" : "none",
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											gap: 2,
										}}>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
												<Box sx={{
													width: 36,
													height: 36,
													borderRadius: "50%",
													bgcolor: "#1a9e8f15",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													flexShrink: 0,
												}}>
													<PersonIcon sx={{ fontSize: 18, color: "#1a9e8f" }} />
												</Box>
												<Box sx={{ minWidth: 0 }}>
													<Typography variant="body2" sx={{ fontWeight: 500, color: "#0f4557", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
														{c.supplier}
													</Typography>
													<Typography variant="caption" sx={{ color: "#5a7a88" }}>
														{c.product} — <span style={{ color: "#1a9e8f", fontWeight: 500 }}>{c.qty}</span>
													</Typography>
												</Box>
											</Box>
											<Box sx={{ textAlign: "right", flexShrink: 0 }}>
												<Typography variant="caption" sx={{ color: "#8e9bae" }}>
													{c.date}
												</Typography>
												<Box sx={{
													mt: 0.5,
													px: 1,
													py: 0.25,
													borderRadius: "12px",
													bgcolor: c.status === "CONFIRMADO" ? "#27ae6015" : "#f5a62315",
													color: c.status === "CONFIRMADO" ? "#27ae60" : "#f5a623",
													fontSize: "0.625rem",
													fontWeight: 500,
												}}>
													{c.status === "CONFIRMADO" ? "Confirmado" : "Pendente"}
												</Box>
											</Box>
										</Box>
									))
								)}
							</Card>
						</Box>

						{/* Weekly Summary */}
						<Box>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
								<Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0f4557" }}>
									Resumo Semanal
								</Typography>
								<Link to="/relatorios" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 4, color: "#1a9e8f", fontSize: "0.875rem" }}>
									Relatórios <ArrowForwardIcon sx={{ fontSize: 14 }} />
								</Link>
							</Box>
							<Card sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8" }}>
								<CardContent sx={{ p: 3 }}>
									{weeklySummary.map((item, i) => (
										<Box key={item.name} sx={{ mb: i < weeklySummary.length - 1 ? 2.5 : 0 }}>
											<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
												<Typography variant="body2" sx={{ color: "#5a7a88" }}>
													{item.name}
												</Typography>
												<Typography variant="body2" sx={{ fontWeight: 500, color: "#0f4557" }}>
													{item.value}
												</Typography>
											</Box>
											<LinearProgress
												variant="determinate"
												value={item.pct}
												sx={{
													height: 8,
													borderRadius: 4,
													bgcolor: "#e5e7eb",
													"& .MuiLinearProgress-bar": {
														borderRadius: 4,
														bgcolor: "#1a9e8f",
													},
												}}
											/>
										</Box>
									))}
								</CardContent>
							</Card>
						</Box>
					</Box>
					)}
			</Box>
			{user?.userId && (
				<UpdatesPopUp usuarioId={user.userId} />
			)}
		</AppLayout>
	);
}

export default Home;
